import React, { FC, ReactElement, useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { mergeStyles, Stack, ThemeProvider } from "@fluentui/react";
import {
  headerStackStyles,
  mainStackStyles,
  rootStackStyles,
  sidebarStackStyles, // Updated
} from "../ux/styles";
import { useAuth0 } from "@auth0/auth0-react";
import ChatPage from "../pages/chatPage";
import Menu from "./chatSidebar";
import LandingPage from "../pages/landingPage";
import DevChatPage from "../pages/devChatPage";
import UserManager from "../pages/UserManager";
import { RdmMain } from "../pages/rdm/rdm_main";
import { CurrentRdm } from "../pages/rdm/current_rdm";
import logo from "../assets/logo.png";
import axios from "axios";
import config from "../config";
import { useMediaQuery } from "react-responsive";
import CSVProcessor from "../components/tester/loadCsv";
import { Theme, getThemeColors } from "../theme/colors";
import {
  activeEntityLookUp,
  dechraEntityLookUp,
  productHierarchyLookup,
} from "../components/hc/entityLookUp";
import { createTheme } from "@fluentui/react";

const Layout: FC = (): ReactElement => {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const isMobile = useMediaQuery({ maxWidth: 414 });
  const [colorMode, setColorMode] = useState<Theme>("dark"); // Lifted colorMode state
  const [qqs, setQqs] = useState<string[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [devNumber, setDevNumber] = useState<number>(0);
  const [autoCompleteDictionary, setAutoCompleteDictionary] = useState<
    Record<string, string> | undefined
  >(undefined);

  const currentThemeColors = getThemeColors(user?.email ?? "", colorMode);
  const theme = createTheme({
    palette: {},
    defaultFontStyle: { fontFamily: currentThemeColors.fontFamily },
  });

  type autoCompleteItem = {
    display_name_in_ui: string;
    field_name: string;
    table_name: string;
    schema_name: string;
    entity_name: string;
  };

  const autocompleteArrayToDictionary = (
    autocompleteArray: autoCompleteItem[]
  ): Record<string, string> => {
    return autocompleteArray.reduce((acc, item) => {
      acc[
        item.display_name_in_ui
      ] = `in schema ${item.schema_name} in table ${item.table_name} field name ${item.field_name} and entity name ${item.entity_name}`;
      return acc;
    }, {} as Record<string, string>);
  };

  const autocompleteSmallerArrayToDictionary = (
    autocompleteArray: autoCompleteItem[]
  ): Record<string, string> => {
    return autocompleteArray.reduce((acc, item) => {
      acc[
        item.display_name_in_ui
      ] = `field name ${item.field_name} and entity name ${item.entity_name}`;
      return acc;
    }, {} as Record<string, string>);
  };

  useEffect(() => {
    const postUsername = async () => {
      if (user && user.email) {
        try {
          const response = await axios.post(`${config.api.baseUrl}/chat/qqs`, {
            username: user.email,
          });
          // Select the @-mention list by the user's assigned database (server-side
          // config), not by hardcoded email — so any provisioned user gets the right
          // list. Mirrors the server's db_database -> list mapping.
          const dbDatabase: string | null = response.data.db_database ?? null;
          if (dbDatabase === "active_international") {
            setAutoCompleteDictionary(
              autocompleteArrayToDictionary(activeEntityLookUp)
            );
          } else if (dbDatabase === "dechraus") {
            setAutoCompleteDictionary(
              autocompleteSmallerArrayToDictionary(dechraEntityLookUp)
            );
          } else if (dbDatabase === "pos_nielsen_1") {
            setAutoCompleteDictionary(
              autocompleteSmallerArrayToDictionary(productHierarchyLookup)
            );
          } else if (user.email === "robert.molnar@oneadvisor1.ai") {
            // Demo account with a custom static dictionary (no db_database mapping).
            setAutoCompleteDictionary({
              "tv-average": "this is the average frj table 5",
              "tv-max": "maximum value from TV table",
              "user-count": "total number of users",
              "user-count-2": "total number of users 2",
              "user-count-3": "total number of users 3",
              "user-count-4": "total number of users 4",
              "user-count-5": "total number of users 5",
            });
          }
          setQqs(
            Object.keys(response.data.rows)
              .filter(
                (key) => key.startsWith("q") && response.data.rows[key] !== null
              )
              .map((key) => response.data.rows[key])
          );
        } catch (error) {
          console.error("Failed to post user email for qqs:", error);
        }
      }
    };

    postUsername();
  }, [user]);

  if (isLoading) {
    return <div>Loading ...</div>;
  }

  const logoutButtonClass = mergeStyles({
    marginRight: "10px",
    borderRadius: "20px",
    padding: "5px 20px",
    cursor: "pointer",
    color: "white",
    border: `1px solid #3B9161`,
    backgroundColor: "#3B9161",
    ":hover": {
      backgroundColor: "#3B9161",
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div style={{ height: "calc(100vh - 48px)" }}>
        {isAuthenticated && (
          <Stack styles={rootStackStyles(colorMode, user?.email ?? "")}>
            <Stack.Item styles={headerStackStyles}>
              {/* <Header username={user?.name ?? ''}></Header> */}
            </Stack.Item>
            <Stack horizontal grow={1}>
              <Stack.Item
                styles={{
                  root: {
                    ...sidebarStackStyles(colorMode, user?.email ?? ""),
                    position: isMobile ? "absolute" : "relative",
                    top: 0,
                    left: 0,
                  },
                }}
              >
                {" "}
                <Menu
                  username={user?.email ?? ""}
                  colorMode={colorMode}
                  setColorMode={setColorMode}
                  isCollapsed={isMenuOpen}
                  setIsCollapsed={setIsMenuOpen}
                  onDevNumberChange={setDevNumber}
                />
              </Stack.Item>
              <Stack.Item
                grow={1}
                style={{ background: colorMode === "light" ? "white" : "black" }}
                styles={{
                  root: {
                    ...mainStackStyles(colorMode, user?.email ?? ""),
                    marginLeft: isMobile ? "3rem" : "0",
                  },
                }}
              >
                <Routes>
                  <Route
                    path="/chat/:chatId"
                    element={
                      <ChatPage
                        theme={colorMode}
                        convoStarters={qqs}
                        username={user?.email ?? ""}
                        isCollapsed={isMenuOpen}
                        devNumber={devNumber}
                        autoCompleteDictionary={autoCompleteDictionary}
                      />
                    }
                  />
                  <Route
                    path="/chat/:chatId/:shared"
                    element={
                      <ChatPage
                        theme={colorMode}
                        convoStarters={qqs}
                        username={user?.email ?? ""}
                        isCollapsed={isMenuOpen}
                        devNumber={devNumber}
                      />
                    }
                  />
                  <Route
                    path="/chat"
                    element={
                      <ChatPage
                        theme={colorMode}
                        convoStarters={qqs}
                        username={user?.email ?? ""}
                        isCollapsed={isMenuOpen}
                        devNumber={devNumber}
                      />
                    }
                  />
                  <Route
                    path="/current_rdm"
                    element={
                      <CurrentRdm
                        username={user?.email ?? ""}
                        theme={colorMode}
                        devNumber={devNumber}
                      />
                    }
                  />
                  {devNumber > 0 && (
                    <>
                      {devNumber === 1 && (
                        <>
                          <Route path="/upload" element={<CSVProcessor />} />
                          <Route
                            path="/dev_chat"
                            element={
                              <DevChatPage
                                theme={colorMode}
                                convoStarters={qqs}
                                username={user?.email ?? ""}
                                isCollapsed={isMenuOpen}
                                devNumber={devNumber}
                              />
                            }
                          />
                        </>
                      )}
                      <Route
                        path="/user_manager"
                        element={
                          <UserManager
                            username={user?.email ?? ""}
                            theme={colorMode}
                            devNumber={devNumber}
                          />
                        }
                      />
                      <Route
                        path="/rdm"
                        element={
                          <RdmMain
                            username={user?.email ?? ""}
                            theme={colorMode}
                            devNumber={devNumber}
                          />
                        }
                      />
                    </>
                  )}
                  <Route path="/" element={<Navigate to="/chat/0" replace />} />
                </Routes>
              </Stack.Item>
            </Stack>
          </Stack>
        )}
        {!isAuthenticated && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100vh - 20px)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "50%",
                justifyContent: "flex-end",
              }}
            >
              <img
                src={logo}
                alt="Logo"
                style={{ height: "40%", width: "auto", marginBottom: "100px" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                height: "50%",
                alignItems: "flex-start",
              }}
            >
              <button
                onClick={() => loginWithRedirect()}
                className={logoutButtonClass}
              >
                Log In
              </button>
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
};

export default Layout;
